import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EvaluationApiService } from '../evaluations/evaluation-api.service';
import {
  Evaluation,
  QUARTERS,
  currentQuarter,
  employeeCanEdit,
  formTypeForRole,
  formTypeLabel,
  isMissingEvaluationApi,
  missingRequiredFields,
  quarterError,
  quarterFromDates,
  quarterLabel,
  quarterRange,
  resultLabel,
  sameQuarter,
  statusMeta,
  toInputDate as evaluationDate
} from '../evaluations/evaluation.model';
import { ApiService } from '../services/api.service';
import { UtilitiesService } from '../services/utilities.service';
import Swal from 'sweetalert2';

interface LeaveForm {
  from_date: string | null;
  to_date: string | null;
  leave_type: string;
  notes: string | null;
  hours: number | null;
}

interface EvaluationDraft {
  id: string | null;
  form_type: string | null;
  year: number | null;
  quarter: number | null;
  period_from: string | null;
  period_to: string | null;
  employee_notes: string;
  answers: Record<string, string | number | null>;
  status: string | null;
  admin_comment: string | null;
}

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
    standalone: false
})
export class ProfileComponent implements OnInit {

  id: any;
  token: any;
  user: any;
  employee: any;

  Swal = 'sweetalert2';

  user_password = {
    id: null,
    email: '',
    password: '',
  };

  slots: any[] = [];

  slot = {
    date: '',
    doctor_id: null,
    reason: '',
    period: 30
  };

  myLeaves: any[] = [];
  leaveBalance: any = null;
  leavesMonths: number | null = null;
  employeeBalances: any[] = [];
  employeeBalanceSearch = '';
  employeeBalancePage = 1;
  employeeBalancePageSize = 10;
  modalMode: 'create' | 'edit' = 'create';
  editingLeaveId: string | null = null;
  submitting = false;
  uploading = false;
  selectedLeave: any = null;
  files: FileList | null = null;

  leaveForm: LeaveForm = this.emptyLeaveForm();
  durationMode: 'days' | 'hours' = 'days';

  myEvaluations: Evaluation[] = [];
  evaluationsLoading = false;
  evaluationsUnavailable = false;
  evaluationSubmitting = false;
  evaluationModalMode: 'create' | 'edit' | 'view' = 'create';
  evaluationDraft: EvaluationDraft = this.emptyEvaluationDraft();
  readonly formTypeLabel = formTypeLabel;
  readonly statusMeta = statusMeta;
  readonly employeeCanEdit = employeeCanEdit;
  readonly quarterLabel = quarterLabel;
  readonly resultLabel = resultLabel;
  readonly quarters = QUARTERS;

  readonly hourOptions = [
    { value: 1, label: '1 hour' },
    { value: 2, label: '2 hours' },
    { value: 3, label: '3 hours' },
    { value: 4, label: '4 hours (half day)' },
    { value: 5, label: '5 hours' },
    { value: 6, label: '6 hours' },
    { value: 7, label: '7 hours' },
    { value: 8, label: '8 hours (full day)' }
  ];

  readonly leaveTypes = [
    { value: 'ANNUAL', label: 'Annual', badge: 'bg-primary' },
    { value: 'SICK', label: 'Sick', badge: 'bg-warning text-dark' },
    { value: 'PERSONAL', label: 'Without Payment', badge: 'bg-secondary' },
    { value: 'LEAVE', label: 'Leave', badge: 'bg-info text-dark' },
    { value: 'LONG', label: 'Long', badge: 'bg-dark' },
    { value: 'OTHER', label: 'Other', badge: 'bg-light text-dark border' }
  ];

  constructor(
    public utility: UtilitiesService,
    private router: Router,
    public api: ApiService,
    private evaluationsApi: EvaluationApiService,
    private modalService: NgbModal
  ) {
    this.utility.show = true;
    this.token = localStorage.getItem('access_token');
    this.id = localStorage.getItem('id');
  }

  ngOnInit(): void {
    this.getUser();
  }

  get isLeaveFormValid(): boolean {
    return !!(
      this.employee &&
      this.leaveForm.from_date &&
      this.leaveForm.to_date &&
      this.leaveForm.leave_type &&
      this.leaveForm.leave_type !== '0' &&
      this.previewDays !== null &&
      this.previewDays > 0
    );
  }

  get previewDays(): number | null {
    if (this.durationMode === 'hours') {
      if (!this.leaveForm.from_date || !this.leaveForm.hours) {
        return this.leaveForm.from_date ? 0 : null;
      }
      return this.leaveForm.hours / 8;
    }
    if (!this.leaveForm.from_date || !this.leaveForm.to_date) {
      return null;
    }
    return this.calcDays(this.leaveForm.from_date, this.leaveForm.to_date);
  }

  setDurationMode(mode: 'days' | 'hours'): void {
    this.durationMode = mode;
    if (mode === 'hours') {
      this.leaveForm.hours = this.leaveForm.hours || 4;
      this.leaveForm.to_date = this.leaveForm.from_date;
    } else {
      this.leaveForm.hours = null;
    }
  }

  onFromDateChange(): void {
    if (this.durationMode === 'hours') {
      this.leaveForm.to_date = this.leaveForm.from_date;
    }
  }

  formatLeaveDuration(leave: any): string {
    const hours = this.parseHours(leave?.hours);
    if (hours) {
      return hours === 4 ? '0.5 day' : `${hours} hrs`;
    }
    return String(leave?.dayDiff ?? 0);
  }

  get filteredEmployeeBalances(): any[] {
    const query = this.employeeBalanceSearch.trim().toLowerCase();
    if (!query) {
      return this.employeeBalances;
    }
    return this.employeeBalances.filter(item =>
      (item.name ?? '').toLowerCase().includes(query) ||
      (item.work_location ?? '').toLowerCase().includes(query) ||
      (item.status ?? '').toLowerCase().includes(query)
    );
  }

  get pagedEmployeeBalances(): any[] {
    const start = (this.employeeBalancePage - 1) * this.employeeBalancePageSize;
    return this.filteredEmployeeBalances.slice(start, start + this.employeeBalancePageSize);
  }

  changeP(content: any, user: any) {
    this.user_password.id = user.id;
    this.user_password.email = user.email;
    this.user_password.password = user.password;

    this.modalService.open(content);
  }

  open(content: any) {
    this.modalService.open(content);
  }

  openCreateLeave(modal: unknown): void {
    if (!this.employee) {
      Swal.fire({
        title: 'Not available',
        text: 'Leave requests are only available for registered employees.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.modalMode = 'create';
    this.editingLeaveId = null;
    this.durationMode = 'days';
    this.leaveForm = this.emptyLeaveForm();
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  openEditLeave(modal: unknown, leave: any): void {
    this.modalMode = 'edit';
    this.editingLeaveId = leave.id;
    const hours = this.parseHours(leave.hours);
    this.durationMode = hours ? 'hours' : 'days';
    this.leaveForm = {
      from_date: this.toInputDate(leave.from_date),
      to_date: this.toInputDate(leave.to_date),
      leave_type: leave.leave_type ?? 'ANNUAL',
      notes: leave.notes,
      hours
    };
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  openLeaveFiles(modal: unknown, leave: any): void {
    this.selectedLeave = leave;
    this.files = null;
    this.modalService.open(modal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  fileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.files = input.files;
  }

  submitLeaveFiles(): void {
    if (!this.selectedLeave?.id) {
      return;
    }

    if (!this.files || this.files.length === 0) {
      Swal.fire({
        title: 'Warning!',
        text: 'No files selected!',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    const formData = new FormData();
    for (const file of Array.from(this.files)) {
      formData.append('file', file, file.name);
    }

    this.uploading = true;
    this.api.upload('leaves/upload/' + this.selectedLeave.id + '/1', formData).subscribe({
      next: () => {
        Swal.fire({
          title: 'Success!',
          text: 'File uploaded successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000
        });
        this.files = null;
        this.loadMyLeaves();
      },
      error: () => {
        Swal.fire({
          title: 'Error!',
          text: 'Could not upload the file. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.uploading = false;
      }
    });
  }

  onDeleteLeaveFile(file: any): void {
    const fileId = this.getFileId(file);
    if (!fileId) {
      return;
    }

    Swal.fire({
      title: 'Delete attachment?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (!result.isConfirmed) {
        return;
      }

      this.api.delete('leaves/upload/' + fileId, this.token).subscribe({
        next: () => {
          Swal.fire({
            title: 'Deleted',
            text: 'Attachment removed.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          this.loadMyLeaves();
        },
        error: () => {
          Swal.fire({
            title: 'Error!',
            text: 'Could not delete this file.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      });
    });
  }

  getLeaveFiles(leave: any): any[] {
    if (!leave) {
      return [];
    }
    const files = leave.files ?? leave.attachments ?? [];
    return Array.isArray(files) ? files : [];
  }

  leaveFileCount(leave: any): number {
    return this.getLeaveFiles(leave).length;
  }

  getFileUrl(file: any): string {
    if (!file) {
      return '';
    }
    if (typeof file === 'string') {
      return file;
    }
    if (typeof file.file === 'string') {
      return file.file;
    }
    if (file.file?.file) {
      return file.file.file;
    }
    return file.url || file.attachment || '';
  }

  getFileId(file: any): string | number | null {
    return file?.id ?? file?.file?.id ?? null;
  }

  getFileName(url: string): string {
    if (!url) {
      return 'Attachment';
    }
    return decodeURIComponent(url.split('/').pop() || 'Attachment');
  }

  getUser() {
    this.utility.loading = true;
    let id = localStorage.getItem('id');
    if (id) {
      const sub = this.api.get('users/' + id, this.token).subscribe(
        async data => {
          let objects: any = {
            users: []
          };
          objects = data;

          this.user = objects.users[0][0];
        },
        async error => { }
      );

      sub.add(() => { this.getDetails(id); });
    }
  }

  getDetails(id: any) {
    const sub = this.api.get('employees/user/' + id, this.token).subscribe(
      async data => {
        let objects: any = {};
        objects = data;

        this.employee = objects;
      },
      async error => { }
    );
    sub.add(() => {
      if (this.utility.isAdmin()) {
        this.loadAllEmployeeBalances();
      }
      if (this.employee?.id) {
        this.loadEmployeeLeaveData();
        this.loadMyEvaluations();
      } else if (!this.utility.isAdmin()) {
        this.utility.loading = false;
      }
    });
  }

  loadEmployeeLeaveData(): void {
    this.loadMyLeaves();
    this.loadLeaveBalance();
  }

  loadAllEmployeeBalances(): void {
    if (!this.utility.isAdmin()) {
      return;
    }

    const manageLoading = !this.employee?.id;
    if (manageLoading) {
      this.utility.loading = true;
    }

    this.api.get('employees/', this.token).subscribe({
      next: (employees: any) => {
        this.api.get('leaves/stats', this.token).subscribe({
          next: (stats: any) => {
            const statsList: any[] = Array.isArray(stats) ? stats : [];
            const empRows: any[] = Array.isArray(employees) ? employees : [];
            const statsMap = new Map(
              statsList.map((row: any) => [String(row.id), row])
            );
            this.employeeBalances = empRows
              .filter((row: any) => {
                const emp = row.employee ?? row;
                const hidden = emp?.hidden === true || emp?.hidden === 1 || emp?.hidden === '1';
                return emp?.id && !hidden && this.isActiveUser(row);
              })
              .map((row: any) => {
                const emp = row.employee ?? row;
                const balance = statsMap.get(String(emp.id));
                return {
                  id: emp.id,
                  name: emp.name,
                  status: emp.status,
                  work_location: emp.work_location,
                  phone: emp.phone ?? row.user?.phone,
                  ANNUAL: balance?.ANNUAL ?? 0,
                  ANNUAL_LEFT: balance?.ANNUAL_LEFT ?? 30,
                  SICK: balance?.SICK ?? 0,
                  SICK_LEFT: balance?.SICK_LEFT ?? 10,
                  PERSONAL: balance?.PERSONAL ?? 0,
                  updated_at: balance?.updated_at ?? null,
                  months: balance?.updated_at ? this.monthsBetween(balance.updated_at) : null
                };
              })
              .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

            this.employeeBalancePage = 1;
            if (manageLoading) {
              this.utility.loading = false;
            }
          },
          error: () => {
            this.onEmployeeBalancesError(manageLoading);
          }
        });
      },
      error: () => {
        this.onEmployeeBalancesError(manageLoading);
      }
    });
  }

  private onEmployeeBalancesError(manageLoading: boolean): void {
    if (manageLoading) {
      this.utility.loading = false;
    }
    Swal.fire({
      title: 'Error',
      text: 'Could not load employee leave balances.',
      icon: 'error',
      confirmButtonText: 'OK'
    });
  }

  onEmployeeBalanceSearch(): void {
    this.employeeBalancePage = 1;
  }

  private isActiveUser(row: any): boolean {
    const user = row?.user ?? row;
    const flag = user?.active ?? user?.is_active;
    return flag === true || flag === 1 || flag === '1';
  }

  loadLeaveBalance(): void {
    if (!this.employee?.id) {
      return;
    }

    this.api.get('leaves/stats/' + this.employee.id, this.token).subscribe({
      next: (data: any) => {
        this.leaveBalance = data ?? null;
        if (this.leaveBalance?.updated_at) {
          this.leavesMonths = this.monthsBetween(this.leaveBalance.updated_at);
        }
      },
      error: () => {
        this.leaveBalance = null;
        this.leavesMonths = null;
      }
    });
  }

  loadMyLeaves(): void {
    if (!this.employee?.id) {
      return;
    }

    this.utility.loading = true;
    this.api.get('leaves/employee/' + this.employee.id, this.token).subscribe({
      next: (data: any) => {
        const leaves: any[] = Array.isArray(data) ? data : [];
        this.myLeaves = leaves
          .map(item => {
            const hours = this.parseHours(item.hours);
            return {
              ...item,
              hours,
              dayDiff: hours ? hours / 8 : this.calcDays(item.from_date, item.to_date)
            };
          })
          .sort((a, b) => new Date(b.from_date).getTime() - new Date(a.from_date).getTime());

        if (this.selectedLeave?.id) {
          this.selectedLeave =
            this.myLeaves.find(item => item.id === this.selectedLeave.id) ?? this.selectedLeave;
        }
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'Could not load your leave requests.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.utility.loading = false;
      }
    });
  }

  getLeaveTypeMeta(type: string) {
    return this.leaveTypes.find(t => t.value === type) ?? {
      value: type,
      label: type,
      badge: 'bg-secondary'
    };
  }

  saveLeave(): void {
    if (!this.isLeaveFormValid || this.submitting || !this.employee) {
      return;
    }

    const body = {
      employee_id: this.employee.id,
      employee_name: this.employee.name ?? '',
      from_date: this.leaveForm.from_date,
      to_date: this.durationMode === 'hours' ? this.leaveForm.from_date : this.leaveForm.to_date,
      leave_type: this.leaveForm.leave_type,
      notes: this.leaveForm.notes,
      hours: this.durationMode === 'hours' ? this.leaveForm.hours : null
    };

    this.submitting = true;
    const request =
      this.modalMode === 'edit' && this.editingLeaveId
        ? this.api.update('leaves/' + this.editingLeaveId, body, this.token)
        : this.api.post('leaves/', body, this.token);

    request.subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.loadEmployeeLeaveData();
        Swal.fire({
          title: 'Success',
          text:
            this.modalMode === 'edit'
              ? 'Leave request updated.'
              : 'Leave request submitted.',
          icon: 'success',
          confirmButtonText: 'OK',
          timer: 2000,
          showConfirmButton: true
        });
      },
      error: () => {
        Swal.fire({
          title: 'Error',
          text: 'Something went wrong. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  getMySlots(id: any) {
    const sub = this.api.get('schedules/' + id, this.token).subscribe(
      async data => {
        let objects: any = {};
        objects = data;

        this.slots = objects.schedules;
      },
      async error => { }
    );
    sub.add(() => { this.utility.loading = false; });
  }

  OnChangePassword(id: any) {
    const body = {
      password: this.user_password.password
    };

    this.api.update("users/password/" + id, body, this.token).subscribe(
      async data => {
        Swal.fire({
          title: 'Success!',
          text: 'Password Changed Successfuly!',
          icon: 'success',
          confirmButtonText: 'Cool'
        });
      },
      async error => {
        Swal.fire({
          title: 'Error!',
          text: 'Something went wrong!',
          icon: 'error',
          confirmButtonText: 'Ok'
        });
      }
    );

    this.modalService.dismissAll();
  }

  createSlot() {
    if (this.employee) {
      const body = {
        date_time: this.slot.date,
        period: this.slot.period,
        active: true,
        blocked: true,
        doctor_id: this.employee.id,
        reason: this.slot.reason,
      };

      const sub = this.api.post("schedules/", body, this.token).subscribe(
        async data => {
          let objects: any = {};
          objects = data;

          Swal.fire({
            title: 'Success!',
            text: 'Slot Booked Successfuly!',
            icon: 'success',
            confirmButtonText: 'Cool'
          });
        },
        async error => {
          Swal.fire({
            title: 'Error!',
            text: 'Something went wrong!',
            icon: 'error',
            confirmButtonText: 'Ok'
          });
        }
      );
    }
    else {
      Swal.fire({
        title: 'info!',
        text: 'You are not a Clinician!',
        icon: 'warning',
        confirmButtonText: 'Ok'
      });
    }
  }

  saveDoctor(doctor: any) {
    localStorage.setItem('doctor', JSON.stringify(doctor));
  }

  monthsBetween(value: string): number {
    const today = new Date();
    const date = new Date(value);
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    return (todayYear - dateYear) * 12 + (todayMonth - dateMonth);
  }

  get evaluationFormType() {
    return formTypeForRole(localStorage.getItem('role'));
  }

  get quarterYears(): number[] {
    const current = new Date().getFullYear();
    const years = [current - 1, current];
    if (this.evaluationDraft.year && !years.includes(this.evaluationDraft.year)) {
      years.push(this.evaluationDraft.year);
    }
    return years.sort((a, b) => a - b);
  }

  loadMyEvaluations(): void {
    if (!this.employee?.id) {
      return;
    }

    this.evaluationsLoading = true;
    this.evaluationsUnavailable = false;
    this.evaluationsApi.mine(this.token).subscribe({
      next: items => {
        this.myEvaluations = items.sort((a, b) => {
          const left = new Date(b.updated_at || b.created_at || 0).getTime();
          const right = new Date(a.updated_at || a.created_at || 0).getTime();
          return left - right;
        });
      },
      error: error => {
        this.myEvaluations = [];
        this.evaluationsUnavailable = isMissingEvaluationApi(error);
        this.evaluationsLoading = false;
        if (!this.evaluationsUnavailable) {
          Swal.fire({
            title: 'Error',
            text: 'Could not load your evaluations.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      },
      complete: () => {
        this.evaluationsLoading = false;
      }
    });
  }

  openEvaluation(modal: unknown, item?: Evaluation): void {
    if (!this.employee) {
      return;
    }

    if (!item) {
      const current = currentQuarter();
      const existing = this.myEvaluations.find(evaluation =>
        evaluation.status !== 'rejected' &&
        sameQuarter(evaluation.period_from, evaluation.period_to, current.year, current.quarter)
      );
      if (existing && !employeeCanEdit(existing.status)) {
        Swal.fire({
          title: 'Already submitted',
          text: 'You already have an evaluation for this quarter.',
          icon: 'info',
          confirmButtonText: 'OK'
        });
        return;
      }
      item = existing;
    }

    if (!item) {
      this.evaluationModalMode = 'create';
      this.evaluationDraft = this.emptyEvaluationDraft();
    } else {
      this.evaluationModalMode = employeeCanEdit(item.status) ? 'edit' : 'view';
      this.evaluationDraft = this.draftFromEvaluation(item);
    }

    this.modalService.open(modal, { size: 'xl', centered: true, backdrop: 'static', scrollable: true });
  }

  onEvaluationTypeChange(next: string): void {
    if (!next || next === this.evaluationDraft.form_type) {
      return;
    }
    const joining = this.evaluationDraft.answers['joining_date'] ?? null;
    const notes = this.evaluationDraft.answers['recommendations'] ?? null;
    this.evaluationDraft.form_type = next;
    this.evaluationDraft.answers = {};
    if (joining) {
      this.evaluationDraft.answers['joining_date'] = joining;
    }
    if (notes) {
      this.evaluationDraft.answers['recommendations'] = notes;
    }
    this.prefillJoiningDate();
  }

  saveEvaluation(submit: boolean): void {
    if (!this.employee || this.evaluationSubmitting || this.evaluationModalMode === 'view') {
      return;
    }

    if (!this.evaluationDraft.form_type) {
      Swal.fire({ title: 'Check the form', text: 'Choose the employee type.', icon: 'warning', confirmButtonText: 'OK' });
      return;
    }

    const invalidPeriod = quarterError(this.evaluationDraft.year, this.evaluationDraft.quarter);
    if (invalidPeriod) {
      Swal.fire({ title: 'Check the form', text: invalidPeriod, icon: 'warning', confirmButtonText: 'OK' });
      return;
    }

    const range = quarterRange(this.evaluationDraft.year!, this.evaluationDraft.quarter!);
    const duplicate = this.myEvaluations.some(evaluation =>
      evaluation.id !== this.evaluationDraft.id &&
      evaluation.status !== 'rejected' &&
      sameQuarter(evaluation.period_from, evaluation.period_to, this.evaluationDraft.year, this.evaluationDraft.quarter)
    );
    if (duplicate) {
      Swal.fire({
        title: 'Already submitted',
        text: 'You already have an evaluation for this quarter.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (submit) {
      const missing = missingRequiredFields(this.evaluationDraft.form_type, this.evaluationDraft.answers);
      if (missing.length > 0) {
        Swal.fire({
          title: 'Check the form',
          text: `Complete “${missing[0].label}” before submitting.`,
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
    }

    const status = submit ? 'submitted' : 'draft';
    const body = {
      employee_id: this.employee.id,
      employee_name: this.employee.name ?? '',
      form_type: this.evaluationDraft.form_type,
      period_from: range?.from ?? null,
      period_to: range?.to ?? null,
      answers: this.evaluationDraft.answers,
      employee_notes: this.noteFromAnswers(),
      status
    };

    this.evaluationSubmitting = true;
    const request = this.evaluationDraft.id
      ? this.evaluationsApi.update(this.evaluationDraft.id, body, this.token)
      : this.evaluationsApi.create(body, this.token);

    request.subscribe({
      next: () => {
        this.modalService.dismissAll();
        this.loadMyEvaluations();
        Swal.fire({
          title: 'Saved',
          text: submit ? 'Evaluation submitted for review.' : 'Evaluation saved as a draft.',
          icon: 'success',
          timer: 1800,
          showConfirmButton: false
        });
      },
      error: error => {
        Swal.fire({
          title: 'Error',
          text: isMissingEvaluationApi(error)
            ? 'The evaluation API is not available on the server yet.'
            : 'Could not save this evaluation.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      },
      complete: () => {
        this.evaluationSubmitting = false;
      }
    });
  }

  private draftFromEvaluation(item: Evaluation): EvaluationDraft {
    const period = quarterFromDates(item.period_from, item.period_to);
    const answers = { ...item.answers };
    if (!answers['recommendations'] && item.employee_notes) {
      answers['recommendations'] = item.employee_notes;
    }
    return {
      id: item.id,
      form_type: item.form_type,
      year: period?.year ?? null,
      quarter: period?.quarter ?? null,
      period_from: evaluationDate(item.period_from),
      period_to: evaluationDate(item.period_to),
      employee_notes: item.employee_notes ?? '',
      answers,
      status: item.status,
      admin_comment: item.admin_comment
    };
  }

  private emptyEvaluationDraft(): EvaluationDraft {
    const current = currentQuarter();
    const draft: EvaluationDraft = {
      id: null,
      form_type: this.evaluationFormType,
      year: current.year,
      quarter: current.quarter,
      period_from: null,
      period_to: null,
      employee_notes: '',
      answers: {},
      status: null,
      admin_comment: null
    };
    this.evaluationDraft = draft;
    this.prefillJoiningDate();
    return this.evaluationDraft;
  }

  private prefillJoiningDate(): void {
    if (this.evaluationDraft.answers['joining_date']) {
      return;
    }
    const raw = this.employee?.joining_date || this.employee?.join_date || this.employee?.date_of_joining;
    const date = evaluationDate(raw);
    if (date) {
      this.evaluationDraft.answers['joining_date'] = date;
    }
  }

  private noteFromAnswers(): string | null {
    const note = this.evaluationDraft.answers['recommendations'];
    if (note === null || note === undefined || note === '') {
      return null;
    }
    return String(note);
  }

  private emptyLeaveForm(): LeaveForm {
    return {
      from_date: null,
      to_date: null,
      leave_type: 'ANNUAL',
      notes: null,
      hours: null
    };
  }

  private parseHours(value: any): number | null {
    const hours = Number(value);
    if (!value || Number.isNaN(hours) || hours <= 0) {
      return null;
    }
    return hours;
  }

  private calcDays(from: string, to: string): number {
    const date1 = new Date(from);
    const date2 = new Date(to);
    const timeDiff = date2.getTime() - date1.getTime();
    return Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
  }

  private toInputDate(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return value;
    }
    return d.toISOString().slice(0, 10);
  }
}
